import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
    DisbursementBeneficiaryType,
    DisbursementEntity,
  } from "../../../api/disbursements";
import {
    selectDisbursementsError,
    selectDisbursementsStatus,
    selectDisbursementsSortedByDate,
} from "./disbursementsSlice";
import {
    fetchDisbursements,
    upsertDisbursements,
} from "./disbursementsThunk";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";


export interface DisbursementRow extends DisbursementEntity {
    isAdded: boolean;
    isDirty: boolean;
}

export interface DisbursementsTableVM {
    rows: DisbursementRow[];
    isLoading: boolean;
    isSaving: boolean;
    error?: string | null;
    hasChanges: boolean;
    hasValidationErrors: boolean;
    loanAccountOptions: OptionNameValuePair<string>[];
    addRow: () => void;
    deleteRow: (rowIndex: number) => void;
    updateRow: (
      rowIndex: number,
      value: unknown,
      field: keyof DisbursementRow,
    ) => void;
    retry: () => void;
    save: () => Promise<void>;
    discard: () => void;
}

export const useDisbursementsTableVM = (): DisbursementsTableVM => {
    const { id: caseId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();
    const accountProductInfo = useAppSelector(getAccountProductInfo, shallowEqual);
    const loanAccountOptions = useMemo<OptionNameValuePair<string>[]>(
      () =>
        accountProductInfo
          .filter((account) => Boolean(account.loan_reference_id))
          .map((account) => ({
            value: account.loan_reference_id,
            name: account.ext_account_number,
            subTitle: account.product,
          })),
      [accountProductInfo],
    );

    const canonicalRows = useAppSelector(selectDisbursementsSortedByDate, shallowEqual);
    const status = useAppSelector(selectDisbursementsStatus);
    const error = useAppSelector(selectDisbursementsError);

    const [localData, setLocalData] = useState<DisbursementRow[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      setLocalData(
        canonicalRows.map((row) => ({
          ...row,
          isAdded: false,
          isDirty: false,
        })),
      );
    }, [canonicalRows]);

    const hasChanges = useMemo(
      () => localData.some((row) => row.isDirty),
      [localData],
    );

    const hasValidationErrors = useMemo(
      () =>
        localData
          .filter((row) => row.isDirty)
          .some((row) => {
            if (!row.loan_account_number) return true;
            if (!row.disbursement_date) return true;
            if (!row.beneficiary_type) return true;
            if (row.disbursement_amount === null || row.disbursement_amount === undefined) {
              return true;
            }

            return Number.isNaN(Number(row.disbursement_amount));
          }),
      [localData],
    );

    const retry = () => {
      if (caseId) {
        dispatch(fetchDisbursements(caseId));
      }
    };

    const addRow = () => {
      const newRow: DisbursementRow = {
        disbursement_id: uuidv4(),
        loan_account_number: "",
        disbursement_date: "",
        beneficiary_type: undefined as unknown as DisbursementBeneficiaryType,
        funds_transfer_mode: null,
        recipient: null,
        disbursement_amount: 0,
        remarks: null,
        isAdded: true,
        isDirty: true,
      };

      setLocalData((prev) => [...prev, newRow]);
    };

    const deleteRow = (rowIndex: number) => {
      setLocalData((prev) => {
        const row = prev[rowIndex];
        if (!row?.isAdded) return prev;

        return prev.filter((_, idx) => idx !== rowIndex);
      });
    };

    const updateRow = (
      rowIndex: number,
      value: unknown,
      field: keyof DisbursementRow,
    ) => {
      setLocalData((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, [field]: value, isDirty: true } : row,
        ),
      );
    };

    const save = async () => {
      if (!caseId) return;

      const dirtyRows = localData.filter((row) => row.isDirty);
      if (dirtyRows.length === 0 || hasValidationErrors) return;

      try {
        setIsSaving(true);

        const disbursementsToUpsert = dirtyRows.map(
          ({ isDirty, isAdded, ...rest }) => rest,
        );

        await dispatch(
          upsertDisbursements({
            case_id: caseId,
            request: {
              disbursements: disbursementsToUpsert,
            },
          }),
        ).unwrap();

        showSuccessToast("Disbursements saved successfully");
      } catch {
        showErrorToast("Failed to save disbursements. Please try again.");
      } finally {
        setIsSaving(false);
      }
    };

    const discard = () => {
      setLocalData(
        canonicalRows.map((row) => ({
          ...row,
          isAdded: false,
          isDirty: false,
        })),
      );
    };

    return {
      rows: localData,
      isLoading: status === "loading" && !isSaving,
      isSaving,
      error,
      hasChanges,
      hasValidationErrors,
      loanAccountOptions,
      addRow,
      deleteRow,
      updateRow,
      retry,
      save,
      discard,
    };
};